import { makeStyles } from '@material-ui/core'
import classnames from 'classnames'
import { useViewShowChecker } from 'ducks/viewMode'
import { useEffect } from 'react'
import { Layout, Title, useErrorHandling, useTranslate } from 'react-admin'
import { connect } from 'react-redux'
import { usePremiumProducts } from 'hooks/usePremiumProducts'
import { useFeatureFlag } from 'util/featureFlags'
import { getRoleFromState } from 'util/misc'
import BottomNavigationMobile from './BottomNavigationMobile'
import MainHeader from './MainHeader'
import RouteAwareSidebar from './RouteAwareSidebar'
import useMenu from './menus/useMenu'

const isShopHardwareRoute = (pathname) => pathname === '/shop' || pathname.startsWith('/shop/')

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#ffffffb3',
    height: '100vh',
  },
  content: ({ zone }) => ({
    //hide this in dashboard
    borderLeft: zone === 'control' ? theme.border.default : 'none',
    background: zone === 'control' ? theme.greyLight1 : '#ffffff', // can we have same background across whole app?
  }),
  /** Shop: keep layout within the viewport so only the main column scrolls (LHS nav stays visible). */
  shopLayoutRoot: {
    overflow: 'hidden',
    maxHeight: '100vh',
  },
  shopAppFrame: {
    minHeight: 0,
    flex: '1 1 auto',
    overflow: 'hidden',
  },
  shopMainContent: {
    minHeight: 0,
    flex: '1 1 auto',
    overflow: 'hidden',
  },
  shopScrollContent: {
    minHeight: 0,
  },
  shopSidebarDrawer: {
    flexShrink: 0,
    alignSelf: 'stretch',
  },
}))

const CustomLayout = (props) => {
  const { theme, children, title, zone, viewAsCustomer, pathname } = props
  const classes = useStyles({ zone })
  const isShopRoute = isShopHardwareRoute(pathname ?? '')
  const showHeader = useViewShowChecker('header')
  const proUXHeader = viewAsCustomer || !showHeader ? null : MainHeader
  const isNav3Enabled = useFeatureFlag('nav_3', 'on')

  // Prefetch wallet products early so Control menu items (e.g. Audit Trail) resolve without waiting on Control mount.
  usePremiumProducts()

  const Menu = useMenu()

  return (
    <Layout
      id={theme}
      isNav3Enabled={isNav3Enabled}
      title={title}
      className={{
        root: classnames(classes.root, isShopRoute && classes.shopLayoutRoot),
        content: classnames(classes.content, isShopRoute && classes.shopScrollContent),
        ...(isShopRoute ? { appFrame: classes.shopAppFrame, mainContent: classes.shopMainContent } : {}),
      }}
      sidebar={RouteAwareSidebar}
      sidebarProps={isShopRoute ? { className: classes.shopSidebarDrawer } : undefined}
      menu={Menu}
      header={proUXHeader}
      bottomNavigation={BottomNavigationMobile}
      error={Error}
      notification={null} // Gets added at CoreAdminUI level, above this
    >
      {children}
    </Layout>
  )
}

export default connect((state) => {
  const role = getRoleFromState(state)
  return {
    zone: state.zone,
    isAdmin: Boolean(role) && role.is_admin,
    isPro: Boolean(role),
    viewAsCustomer: state.viewAsCustomer,
    pathname: state.router?.location?.pathname ?? '',
  }
})(CustomLayout)

// Accroding to https://marmelab.com/react-admin/doc/4.2/Layout.html#error
// If you want to customize this page, or log the error to a third-party service
// create your own <Error> component, and pass it to a custom Layout
const Error = ({ error }) => {
  const translate = useTranslate()
  const errorHandling = useErrorHandling()
  // log error to Sentry
  useEffect(() => {
    errorHandling.unexpected(error, { notify: false })
  }, [error])

  return (
    <div>
      <Title title="Error" />
      <h1>{translate('ra.notification.unexpected_error')}</h1>
    </div>
  )
}
